import { useEffect, useRef, useState } from "react";
import { Bell, BellOff, Loader2, MessageCircle, Plus, Send, Sparkles, Trash2, User, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ReWhatsAppCard } from "./ReWhatsAppCard";

const STATUS_COLORS = {
  active: "bg-blue-100 text-blue-700",
  handoff: "bg-emerald-100 text-emerald-700",
  booked: "bg-violet-100 text-violet-700",
  needs_human: "bg-rose-100 text-rose-700",
};

// Short WebAudio chime for new-lead alerts (no asset needed).
const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [880, 1180].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = freq;
      const t0 = ctx.currentTime + i * 0.16;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.28);
      o.start(t0); o.stop(t0 + 0.3);
    });
    setTimeout(() => ctx.close(), 800);
  } catch { /* audio not available */ }
};

const AiToggle = ({ label, on, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} type="button"
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${on ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-[#E5D9CF] bg-white text-[#9A8E85]"}`}>
    <span className={`h-2 w-2 rounded-full ${on ? "bg-emerald-500" : "bg-[#C9B8A8]"}`} />
    {label} · {on ? "on" : "off"}
  </button>
);

// AI inbox — every conversation Sara has had on the website widget + WhatsApp.
// Staff can start new WhatsApp conversations, reply manually, and AI-draft replies.
export const ReChatsTab = () => {
  const [chats, setChats] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [startForm, setStartForm] = useState({ phone: "", name: "", message: "" });
  const [starting, setStarting] = useState(false);
  const [alertsOn, setAlertsOn] = useState(() => localStorage.getItem("ds_chat_alerts") === "1");
  const scrollRef = useRef(null);
  const prevUnreadRef = useRef(-1);
  const baseTitleRef = useRef("");

  // Keep the transcript pinned to the newest message (like a real chat app).
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active?.id, active?.messages?.length]);

  const load = async (keepId) => {
    const { data } = await api.get("/admin/realestate/chats").catch(() => ({ data: [] }));
    setChats(data);
    if (keepId) setActive(data.find((c) => c.id === keepId) || null);
    return data;
  };
  useEffect(() => { load(); }, []);

  // Poll for new messages so unread counts, alerts and the tab title stay live.
  useEffect(() => {
    const t = setInterval(() => { load(active?.id); }, 12000);
    return () => clearInterval(t);
  }, [active?.id]);

  const remove = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    try { await api.delete(`/admin/realestate/chats/${id}`); setActive(null); await load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const startConvo = async () => {
    if (!startForm.phone.trim()) { toast.error("Enter a phone number"); return; }
    setStarting(true);
    try {
      const { data } = await api.post("/admin/realestate/chats/start", startForm);
      toast.success(data.sent ? "Message sent on WhatsApp" : "Conversation created (WhatsApp not delivered — check number/window)");
      setShowStart(false);
      setStartForm({ phone: "", name: "", message: "" });
      await load(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setStarting(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/realestate/chats/${active.id}/reply`, { text: reply });
      toast[data.sent ? "success" : "message"](data.sent ? "Sent on WhatsApp" : "Saved (WhatsApp not delivered)");
      setReply("");
      await load(active.id);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setSending(false); }
  };

  const aiSuggest = async () => {
    if (!active) return;
    setSuggesting(true);
    try {
      const { data } = await api.post(`/admin/realestate/chats/${active.id}/ai-suggest`);
      setReply(data.suggestion || "");
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setSuggesting(false); }
  };

  const resumeAi = async () => {
    if (!active) return;
    try { await api.post(`/admin/realestate/chats/${active.id}/resume-ai`); await load(active.id); toast.success("Sara is back on this chat"); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const openChat = async (c) => {
    setActive(c);
    setReply("");
    if (c.unread > 0) {
      setChats((prev) => prev.map((x) => (x.id === c.id ? { ...x, unread: 0 } : x)));
      try { await api.post(`/admin/realestate/chats/${c.id}/read`); } catch { /* non-blocking */ }
    }
  };

  const toggleAi = async (field) => {
    if (!active) return;
    const next = active[field] === false; // currently off → turn on, else turn off
    setActive((a) => ({ ...a, [field]: next }));
    setChats((prev) => prev.map((x) => (x.id === active.id ? { ...x, [field]: next } : x)));
    try {
      await api.patch(`/admin/realestate/chats/${active.id}/ai-settings`, { [field]: next });
      toast.success(`${field === "ai_reply_enabled" ? "AI Auto-Reply" : "AI Follow-ups"} ${next ? "on" : "off"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e)); load(active.id); }
  };

  const totalUnread = (chats || []).reduce((n, c) => n + (c.unread || 0), 0);

  // New-lead alerts: chime + desktop notification whenever total unread rises.
  useEffect(() => {
    if (chats === null) return;
    if (prevUnreadRef.current !== -1 && totalUnread > prevUnreadRef.current && alertsOn) {
      playChime();
      if ("Notification" in window && Notification.permission === "granted") {
        try {
          new Notification("New WhatsApp message", {
            body: "A customer just messaged Sara — open Conversations to reply.",
            icon: "/assets/docstate-mark.png",
          });
        } catch { /* ignore */ }
      }
    }
    prevUnreadRef.current = totalUnread;
  }, [totalUnread, alertsOn, chats]);

  // Reflect the unread count in the browser tab title (visible across tabs).
  useEffect(() => {
    if (!baseTitleRef.current) baseTitleRef.current = document.title.replace(/^\(\d+\)\s*/, "");
    document.title = totalUnread > 0 ? `(${totalUnread}) ${baseTitleRef.current}` : baseTitleRef.current;
    return () => { if (baseTitleRef.current) document.title = baseTitleRef.current; };
  }, [totalUnread]);

  const toggleAlerts = async () => {
    const next = !alertsOn;
    if (next) {
      if ("Notification" in window && Notification.permission === "default") {
        try { await Notification.requestPermission(); } catch { /* ignore */ }
      }
      playChime(); // this user gesture also unlocks the audio context
    }
    setAlertsOn(next);
    localStorage.setItem("ds_chat_alerts", next ? "1" : "0");
    toast.success(next ? "New-lead alerts on" : "Alerts off");
  };

  if (chats === null) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  // Hottest first: chats needing a human, then unread, then most recent.
  const heat = (c) => (c.needs_human || c.ai_paused ? 2 : 0) + (c.unread > 0 ? 1 : 0);
  const sortedChats = [...chats].sort((a, b) => heat(b) - heat(a) || (b.updated_at || "").localeCompare(a.updated_at || ""));

  return (
    <div data-testid="re-chats-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-semibold text-[#1A1615]">Conversations</h2>
          <p className="mt-1 text-xs text-[#91837C]">{chats.length} chats with Sara (web + WhatsApp) · {chats.filter((c) => c.status === "handoff").length} handed off{totalUnread > 0 ? ` · ${totalUnread} unread` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleAlerts} data-testid="re-chat-alerts-toggle"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[11px] font-semibold transition-colors ${alertsOn ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-[#E5D9CF] bg-white text-[#9A8E85] hover:bg-[#F5EBE6]"}`}>
            {alertsOn ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />} Alerts {alertsOn ? "on" : "off"}
          </button>
          <button onClick={() => setShowStart((v) => !v)} data-testid="re-chat-new"
            className="inline-flex items-center gap-2 rounded-full bg-[#1A1615] px-5 py-2.5 text-xs font-medium text-[#F3E5AB] transition-transform hover:-translate-y-0.5">
            <Plus className="h-4 w-4" /> New WhatsApp conversation
          </button>
        </div>
      </div>

      {showStart && (
        <div className="glass mt-4 rounded-2xl p-5" data-testid="re-chat-start-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={startForm.phone} onChange={(e) => setStartForm({ ...startForm, phone: e.target.value })} placeholder="Phone (with country code, e.g. 9715...)" data-testid="re-chat-start-phone" className="rounded-xl border border-[#EBE3DA] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#C9A24B]" />
            <input value={startForm.name} onChange={(e) => setStartForm({ ...startForm, name: e.target.value })} placeholder="Name (optional)" data-testid="re-chat-start-name" className="rounded-xl border border-[#EBE3DA] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#C9A24B]" />
          </div>
          <textarea value={startForm.message} onChange={(e) => setStartForm({ ...startForm, message: e.target.value })} rows={3} placeholder="First message to send on WhatsApp…" data-testid="re-chat-start-message" className="mt-3 w-full resize-none rounded-xl border border-[#EBE3DA] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#C9A24B]" />
          <div className="mt-3 flex gap-2">
            <button onClick={startConvo} disabled={starting} data-testid="re-chat-start-submit" className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] px-5 py-2.5 text-xs font-semibold text-[#1A1615] disabled:opacity-60">
              {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Start & send
            </button>
            <button onClick={() => setShowStart(false)} className="rounded-full border border-[#EBE3DA] px-4 py-2.5 text-xs text-[#5A504B]">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-5"><ReWhatsAppCard /></div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[360px_1fr]">
        {/* list */}
        <div className="space-y-2.5" data-testid="re-chats-list">
          {sortedChats.map((c) => {
            const lead = c.lead || {};
            return (
              <button key={c.id} onClick={() => openChat(c)} data-testid={`re-chat-${c.id}`}
                className={`w-full rounded-2xl border p-4 text-left transition-all ${active?.id === c.id ? "border-[#C9A24B] bg-[#F8F3E6]" : c.unread > 0 ? "border-emerald-300 bg-white hover:border-emerald-400" : "border-[#EBE3DA] bg-white hover:border-[#C9A24B]/50"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`truncate text-sm ${c.unread > 0 ? "font-bold text-[#1A1615]" : "font-semibold text-[#1A1615]"}`}>{lead.name || "Website visitor"}</span>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {c.unread > 0 && <span data-testid={`re-chat-unread-${c.id}`} className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{c.unread}</span>}
                    <span className={`rounded-full px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.1em] ${STATUS_COLORS[c.status] || STATUS_COLORS.active}`}>{c.status}</span>
                  </div>
                </div>
                <p className="mt-1 truncate text-[11px] text-[#91837C]">
                  {[lead.intent, lead.community, lead.budget].filter(Boolean).join(" · ") || "No details captured yet"}
                </p>
                <p className="mt-1 text-[10px] text-[#B5A99E]">{c.channel === "whatsapp" ? "WhatsApp · " : ""}{(c.messages || []).length} messages · {c.updated_at ? new Date(c.updated_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</p>
              </button>
            );
          })}
          {!chats.length && <p className="glass rounded-2xl p-6 text-sm font-light text-[#91837C]">No conversations yet.</p>}
        </div>

        {/* transcript */}
        <div className="glass flex min-h-[300px] flex-col rounded-2xl p-6" data-testid="re-chat-transcript">
          {!active ? (
            <div className="flex h-full min-h-[240px] flex-col items-center justify-center text-center text-sm font-light text-[#91837C]">
              <MessageCircle className="mb-3 h-8 w-8 text-[#D8CBBD]" />
              Select a conversation to read the transcript and reply.
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 border-b border-[#EBE3DA] pb-4">
                <div>
                  <p className="font-serif text-lg font-semibold text-[#1A1615]">{active.lead?.name || "Website visitor"}</p>
                  <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
                    {Object.entries(active.lead || {}).filter(([, v]) => v).map(([k, v]) => (
                      <span key={k} className="rounded-full bg-white px-2.5 py-0.5 text-[#5A504B]"><b className="text-[#1A1615]">{k}</b> {String(v)}</span>
                    ))}
                  </div>
                </div>
                <button onClick={() => remove(active.id)} aria-label="Delete" data-testid="re-chat-delete" className="flex h-8 w-8 items-center justify-center rounded-full border border-red-200 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="re-chat-ai-controls">
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#B5A99E]">Sara on this chat:</span>
                <AiToggle label="AI Auto-Reply" on={active.ai_reply_enabled !== false} onClick={() => toggleAi("ai_reply_enabled")} testid="re-chat-toggle-reply" />
                <AiToggle label="AI Follow-ups" on={active.ai_followup_enabled !== false} onClick={() => toggleAi("ai_followup_enabled")} testid="re-chat-toggle-followup" />
              </div>
              {(active.ai_paused || active.needs_human) && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5" data-testid="re-chat-human-banner">
                  <p className="text-xs font-medium text-rose-700">Human takeover active — Sara is paused on this chat. Reply below as yourself.</p>
                  <button onClick={resumeAi} data-testid="re-chat-resume-ai" className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-3.5 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-rose-700">
                    <Sparkles className="h-3 w-3" /> Resume Sara
                  </button>
                </div>
              )}
              <div ref={scrollRef} className="mt-4 flex-1 space-y-3 overflow-y-auto pr-1" style={{ maxHeight: "52vh" }} data-testid="re-chat-messages">
                {(active.messages || []).map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#1A1615] text-[#F3E5AB]" : "bg-white text-[#1A1615]"}`}>
                      <p className="mb-0.5 flex items-center gap-1 text-[9px] font-semibold uppercase tracking-[0.1em] opacity-60">
                        <User className="h-2.5 w-2.5" /> {m.role === "user" ? "Client" : (m.by === "agent" ? "You" : "Sara")}
                      </p>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>

              {/* reply composer */}
              <div className="mt-4 border-t border-[#EBE3DA] pt-4">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder="Type a reply to send on WhatsApp…" data-testid="re-chat-reply-input"
                  className="w-full resize-none rounded-xl border border-[#EBE3DA] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#C9A24B]" />
                <div className="mt-2 flex items-center gap-2">
                  <button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="re-chat-reply-send"
                    className="inline-flex items-center gap-2 rounded-full bg-[#1A1615] px-5 py-2.5 text-xs font-medium text-[#F3E5AB] disabled:opacity-50">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
                  </button>
                  <button onClick={aiSuggest} disabled={suggesting} data-testid="re-chat-ai-suggest"
                    className="inline-flex items-center gap-2 rounded-full border border-[#C9A24B]/40 px-4 py-2.5 text-xs font-medium text-[#8A6D1F] transition-colors hover:bg-[#C9A24B]/10 disabled:opacity-50">
                    {suggesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI draft
                  </button>
                  {reply && <button onClick={() => setReply("")} aria-label="Clear" className="ml-auto flex h-8 w-8 items-center justify-center rounded-full text-[#C9B8A8] hover:text-red-500"><X className="h-4 w-4" /></button>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
